<template>
  <div class="pd-auth-page">
    <div class="pd-auth-container">
      <!-- Title -->
      <h1 class="pd-auth-title">會員登入</h1>

      <!-- Form -->
      <form @submit.prevent="handleSignIn" class="pd-auth-form">
        <div class="pd-form-group">
          <label for="email-input">電子郵件</label>
          <input
            id="email-input"
            v-model="email"
            type="email"
            placeholder="your@email.com"
            :disabled="isLoading"
            required
          />
        </div>

        <div class="pd-form-group">
          <label for="password-input">密碼</label>
          <input
            id="password-input"
            v-model="password"
            type="password"
            placeholder="••••••••"
            :disabled="isLoading"
            required
          />
        </div>

        <div class="pd-form-options">
          <router-link to="/forgot-password" class="pd-link">
            忘記密碼？
          </router-link>
        </div>

        <button
          type="submit"
          class="pd-btn-primary pd-btn-block"
          :disabled="isLoading"
        >
          <span v-if="!isLoading">登入</span>
          <span v-else>登入中...</span>
        </button>
      </form>

      <!-- Divider -->
      <div class="pd-divider">
        <span>或</span>
      </div>

      <!-- Social Login -->
      <div class="pd-social-login">
        <button @click="signInWithGoogle" class="pd-btn-social pd-btn-google" :disabled="isLoading" aria-label="使用 Google 帳號登入">
          <i class="fab fa-google" aria-hidden="true"></i>
          使用 Google 登入
        </button>
      </div>

      <!-- Sign Up Link -->
      <div class="pd-auth-footer">
        <p>還沒有帳號？<router-link to="/signup">立即註冊</router-link></p>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from '@/utils/helpers'
import { supabase } from '@/supabase'

export default {
  name: 'SignIn',
  data () {
    return {
      email: '',
      password: '',
      isLoading: false
    }
  },
  methods: {
    async handleSignIn () {
      if (this.isLoading) return

      this.isLoading = true
      try {
        // Use Supabase v1 API
        const { user, session, error } = await supabase.auth.signIn({
          email: this.email,
          password: this.password
        })

        if (error) {
          throw error
        }

        if (user && session) {
          await this.$store.dispatch('fetchCurrentUser')

          this.$router.push('/home').catch(err => {
            // Ignore navigation duplicated error
            if (err.name !== 'NavigationDuplicated') {
              throw err
            }
          })
        }
      } catch (error) {
        console.error('登入失敗 - 完整錯誤:', error)
        console.error('錯誤訊息:', error.message)
        console.error('錯誤代碼:', error.code)
        console.error('錯誤狀態:', error.status)

        let errorMessage = '請檢查您的帳號密碼'

        if (error.message.includes('Invalid login credentials')) {
          errorMessage = '帳號或密碼錯誤'
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = '請先驗證您的電子郵件'
        } else if (error.message) {
          errorMessage = error.message
        }

        Toast.fire({
          type: 'error',
          title: `登入失敗：${errorMessage}`
        })
      } finally {
        this.isLoading = false
      }
    },
    async signInWithGoogle () {
      if (this.isLoading) return

      this.isLoading = true
      console.log('開始 Google 登入流程...')

      try {
        console.log('呼叫 Supabase signIn with Google...')
        // Use Supabase v1 OAuth API with explicit redirectTo
        const { error } = await supabase.auth.signIn(
          { provider: 'google' },
          {
            redirectTo: `${window.location.origin}/auth/callback`
          }
        )

        console.log('OAuth 回應 error:', error)

        if (error) {
          throw error
        }

        // OAuth will redirect, so we don't need to do anything here
        console.log('Google OAuth 重新導向中...')
      } catch (error) {
        console.error('Google 登入失敗 - 完整錯誤:', error)
        console.error('錯誤訊息:', error.message)
        Toast.fire({
          type: 'error',
          title: 'Google 登入失敗，請稍後再試'
        })
        this.isLoading = false
      }
    },
  }
}
</script>

<style scoped>
.pd-auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--pd-black);
  padding: 2rem 1rem;
  padding-top: calc(var(--navbar-height) + 2rem);
}

.pd-auth-container {
  max-width: 420px;
  width: 100%;
  background: var(--pd-white);
  padding: 3rem 2.5rem;
  text-align: center;
}

.pd-auth-logo {
  margin-bottom: 2rem;
}

.pd-auth-logo img {
  height: 60px;
  filter: invert(1);
}

.pd-auth-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: var(--pd-black);
  letter-spacing: 0.05em;
}

.pd-form-group {
  margin-bottom: 1.5rem;
  text-align: left;
}

.pd-form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--pd-black);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.pd-form-group input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid var(--pd-black);
  background: var(--pd-white);
  font-size: 1rem;
  transition: all 0.2s ease;
}

.pd-form-group input:focus {
  outline: none;
  border-color: var(--pd-gray-dark);
}

.pd-form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
}

.pd-form-options {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
}

.pd-link {
  color: var(--pd-black);
  text-decoration: underline;
  font-weight: 500;
}

.pd-link:hover {
  opacity: 0.7;
}

.pd-btn-primary {
  width: 100%;
  padding: 1rem;
  background: var(--pd-black);
  color: var(--pd-white);
  border: none;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.pd-btn-primary:hover:not(:disabled) {
  opacity: 0.8;
}

.pd-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pd-divider {
  margin: 2rem 0;
  position: relative;
  text-align: center;
}

.pd-divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e0e0e0;
}

.pd-divider span {
  position: relative;
  background: var(--pd-white);
  padding: 0 1rem;
  color: var(--pd-gray-medium);
  font-size: 0.875rem;
}

.pd-social-login {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pd-btn-social {
  width: 100%;
  padding: 0.875rem 1rem;
  background: var(--pd-white);
  color: var(--pd-black);
  border: 2px solid var(--pd-black);
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
}

.pd-btn-social:hover:not(:disabled) {
  background: var(--pd-black);
  color: var(--pd-white);
}

.pd-btn-social:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pd-btn-social i {
  font-size: 1.125rem;
}

.pd-btn-google:hover:not(:disabled) {
  background: #4285f4;
  border-color: #4285f4;
  color: var(--pd-white);
}

.pd-auth-footer {
  margin-top: 2rem;
  font-size: 0.875rem;
  color: var(--pd-gray-medium);
}

.pd-auth-footer a {
  color: var(--pd-black);
  font-weight: 600;
  text-decoration: underline;
}

.pd-auth-footer a:hover {
  opacity: 0.7;
}

@media (max-width: 480px) {
  .pd-auth-container {
    padding: 2rem 1.5rem;
  }

  .pd-auth-title {
    font-size: 1.5rem;
  }

  .pd-auth-logo img {
    height: 50px;
  }
}
</style>
